import {
  useMutation, useQueryClient,
  type QueryKey, type UseMutationResult,
} from '@tanstack/react-query'

import type { AdminApiClient } from '../api/AdminApiClient'
import { useAdminApiClient } from '../api/AdminApiClientContext'
import type { SuccessEnvelope } from '../api/types'

// Thin wrapper around `useMutation` for admin write actions:
//   1. Injects the AdminApiClient via context so hosts don't need to
//      thread it through props.
//   2. Auto-invalidates a caller-supplied list of query keys on
//      success — the central pattern in plan-admin/06 (every audited
//      action needs to refresh the affected detail + parent list).
//   3. Unwraps `SuccessEnvelope<T>.data` so call sites read just the
//      payload (the AdminApiClient itself preserves the full envelope
//      so paginated metadata still round-trips for queries).
//
// Errors propagate untouched: callers receive the raw `AdminApiError`
// subclass that the AdminApiClient threw, so a host page (or the
// ConfirmActionDialog) can branch on `errorCode`.

export interface UseAdminMutationArgs<TVars, TData> {
  mutate: (
    client: AdminApiClient,
    variables: TVars,
  ) => Promise<SuccessEnvelope<TData>>
  // Each entry is invalidated independently so a single mutation can
  // refresh multiple unrelated caches (e.g. detail + parent list).
  invalidates?: QueryKey[]
}

export type AdminMutationResult<TVars, TData> = UseMutationResult<
  TData, Error, TVars
>

export function useAdminMutation<TVars = void, TData = unknown>(
  args: UseAdminMutationArgs<TVars, TData>,
): AdminMutationResult<TVars, TData> {
  const client = useAdminApiClient()
  const queryClient = useQueryClient()
  return useMutation<TData, Error, TVars>({
    mutationFn: async (variables) => {
      const envelope = await args.mutate(client, variables)
      return envelope.data
    },
    onSuccess: () => {
      for (const key of args.invalidates ?? []) {
        queryClient.invalidateQueries({ queryKey: key })
      }
    },
  })
}
